import React, {useEffect} from 'react'
import OdxIcon from '../NativeOdx/Odx-Icon'
import {odxIconSets} from '../constants'
import OdxDropdown from '../NativeOdx/Odx-Dropdown'
import {Link} from "react-router-dom";
import {isExternalModule} from "../../Utils/modules";
import {IModule} from "../../BackendFacade/IModuleInstances";
import {isUserroleSufficient} from "../../Utils/authorization";
import {UserRoles} from "../../Utils";
import {HubPathRouting} from "../../HubFramework/pathBuilder";
import {simplifyForHtmlId} from "../../Utils/transformer";

interface IContextMenuAction {
    label: string,
    onClick: (event: MouseEvent) => void,
    odxIcon: { iconName: string, iconSet: odxIconSets }
}

interface ModuleOverviewCardProps {
    title: string,
    description: string,
    imageSrc?: string,
    contextMenuActions?: IContextMenuAction[]
    module: IModule,
    idPrefix?: string
}

const ModuleOverviewCard: React.FC<ModuleOverviewCardProps> = ({
                                                                   title,
                                                                   description,
                                                                   imageSrc,
                                                                   contextMenuActions,
                                                                   module,
                                                                   idPrefix
                                                               }) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = React.useState(false);

    useEffect(() => {
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    const handleClickOutside = (event: MouseEvent) => {
        if (isOpen && containerRef.current && !containerRef.current.contains(event.target as Node)) {
            setIsOpen(false);
        }
    };

    const actions = contextMenuActions ?? [];

    const actionId = (actionLabel: string, index: number) =>
        idPrefix ? `${idPrefix}-action-${simplifyForHtmlId(actionLabel || `action-${index}`)}` : undefined;

    const dropdownContent = (
        <div role="listbox" className="odx-dropdown__option-list !p-1.5 text-left">
            {actions.map((option, key) => (
                <div
                    key={key}
                    role="option"
                    className={`odx-select-option odx-option p-0.5}`}
                    id={actionId(option.label, key)}
                    onClick={(event) => {
                        event.stopPropagation()
                        event.preventDefault()
                        option.onClick(event);
                        setIsOpen(false);
                    }}
                >
                    <div className={"flex items-center gap-2"}>
                        <OdxIcon name={option.odxIcon.iconName} size={"md"} iconSet={option.odxIcon.iconSet}/>
                        <span className={"!text-3xl"}> {option.label}</span>
                    </div>
                </div>
            ))}
        </div>
    );

    /**
     * Creates an url for each module card
     * @param module
     */
    const createCardUrl = (module: IModule) => {
        let parameters = module.linkedId ? "?linkedId=" + module.linkedId.projectId : ""
        return "../" + module.staticModuleId + "/" + module.moduleInstanceId + parameters
    }

    return (
        <Link to={createCardUrl(module)}
              target={`${isExternalModule(module) ? "_blank" : ""}`}
              rel={`${isExternalModule(module) ? "noopener noreferrer" : ""}`}
              relative={"route"}
              id={idPrefix ? `${idPrefix}-link` : undefined}
        >
            <div className="odx-card--launch-tile odx-cdk-interactive odx-card !flex flex-col h-full !w-full"
                 id={idPrefix ? `${idPrefix}-card` : undefined}>

                <div className="odx-card__container grow">

                    <div className={"flex w-full"}>

                        <div className={"w-fit !pr-[16px]"}>
                            <odx-avatar
                                size="xxl"
                                shape="circle"
                                variant="ghost"
                                className="flex items-center justify-center"
                                style={{"--size": "64px"} as React.CSSProperties}
                                id={idPrefix ? `${idPrefix}-avatar` : undefined}
                            >
                                {imageSrc ? (
                                    <img
                                        src={imageSrc}
                                        alt={`${title} icon`}
                                        className="object-cover !w-[70%] !h-[70%]"
                                    />
                                ) : (
                                    <odx-icon name="core::folder" size="xxl"></odx-icon>
                                )}
                            </odx-avatar>
                        </div>
                        <div className={"w-full"}>
                            <div className={"odx-card__content w-full !flex h-full"}>
                                <div className={"odx-card__title"} id={idPrefix ? `${idPrefix}-title` : undefined}>
                                    {title}
                                </div>
                                <div className={"odx-card__subtitle"} id={idPrefix ? `${idPrefix}-description` : undefined}>
                                    {description}
                                </div>
                            </div>
                        </div>


                        {actions.length > 0 && isUserroleSufficient(UserRoles.editor, HubPathRouting.currentProject?.id) &&
                            (<div>
                                    <div ref={containerRef}>
                                        <odx-icon-button
                                            className="!-mt-4 active:!bg-[#22AAFF26] focus:!bg-[#22AAFF26]"
                                            variant="ghost"
                                            icon={`${odxIconSets.core}::more`}
                                            size="md"
                                            onClick={(e: any) => {
                                                e.stopPropagation()
                                                e.preventDefault()
                                                setIsOpen(!isOpen)
                                            }}
                                            id={idPrefix ? `${idPrefix}-menu-btn` : undefined}
                                        />
                                    </div>

                                    {isOpen &&
                                        <OdxDropdown
                                            content={dropdownContent}
                                            position="bottom-end"
                                            matchReferenceWidth={false}
                                            offset={0}
                                            showArrow={false}
                                            isOpen={isOpen}
                                            setIsOpen={setIsOpen}
                                            buttonRef={containerRef}
                                            id={idPrefix ? `${idPrefix}-menu` : undefined}
                                        />
                                    }
                                </div>
                            )
                        }
                    </div>
                </div>
            </div>
        </Link>

    )
}

export default ModuleOverviewCard;
